import { buildAgoraParticipantRecords, windowElapsedMs } from './agoraRollup';
import { drainAgoraSamples, hasAgoraSamples, startAgoraSampler } from './agoraSampler';
import { EventHeartbeatGetters } from './getters';
import { EventHeartbeatMutations } from './mutations';
import {
  EventHeartbeatState,
  EVENT_HEARTBEAT_INTERVAL,
  HEALTH_DIAGNOSTIC_COOLDOWN_MS,
  BroadcastConnectionLifecycleState,
  HealthDiagnosticSignal,
  POOR_NETWORK_QUALITY_THRESHOLD,
  STREAM_HEALTH_THRESHOLDS
} from './types';
import { Broadcast } from '../event/types';
import { CurrentUser } from '../user/types';

import analytics from '@/analytics';
import { RECORDING_STUDIO_STATE } from '@/constants';
import { MediaRoom, MediaUserRole } from '@/services/liveMedia.service';
import loggerService from '@/services/logger.service';
import { isOnDemandVideoPlaying, videoLength, videoSeekPosition } from '@/services/onDemandVideo.service';
import trackerService from '@/services/tracker.service';
import { RootState } from '@/store';
import { getNetworkQualityDatapoints } from '@/utils/networkQuality';
import { deepClone } from '@/utils/objectUtil';
import { getAggregate } from '@/utils/stats';

export interface EventHeartbeatContext {
  state: EventHeartbeatState;
  getters: EventHeartbeatGetters;
  commit: <k extends keyof EventHeartbeatMutations>(mutation: k, payload?: any, config?: { root: boolean }) => any;
  dispatch: (action: string, params?: any, settings?: { root: boolean }) => Promise<any>;
  rootState: RootState;
  rootGetters: { [k: string]: any };
}

const actions = {
  initEventHeartbeat(context: EventHeartbeatContext) {
    if (context.state.metadata.session_id) {
      return;
    }

    context.commit('setHeartbeatEventInfo', context.rootState.event);
    context.commit('setHeartbeatClientDeviceInfo');

    const collectHeartbeat = () => {
      context.dispatch('collectHeartbeatMetadata');
      context.dispatch('collectHeartbeatCurrentScreenState');
      context.dispatch('collectHeartbeatUserData');
      context.dispatch('collectHeartbeatQos');
    };

    if (context.rootState.event.event.id) {
      context.commit('stopEventHeartbeat');

      startAgoraSampler(
        () => context.rootGetters['mediaroom/currentMediaRoom'] as MediaRoom,
        () => context.rootState.mediaroom.signalMetadata?.networkQuality
      );

      const intervalId = setInterval(() => {
        const isEventHeartbeatFastEnabled = context.rootState.event?.isEventHeartbeatFastEnabled ?? false;
        collectHeartbeat();
        const heartbeatPayload = deepClone(context.state);
        delete heartbeatPayload.heartbeatIntervalId;
        analytics.track('event_heartbeat', heartbeatPayload);
        if (isEventHeartbeatFastEnabled) {
          const heartbeatFastPayload = getEventHeartbeatFastPayload(heartbeatPayload, context);
          delete (heartbeatFastPayload as any).heartbeatIntervalId;
          analytics.track('event_heartbeat_fast', heartbeatFastPayload);
        }
      }, EVENT_HEARTBEAT_INTERVAL);
      context.commit('setHeartbeatIntervalId', intervalId);
    }
  },
  collectHeartbeatMetadata(context: EventHeartbeatContext) {
    const payload_counter = context.state.metadata.payload_counter + 1;
    let sessionId = window.sessionStorage.getItem('segmentSessionId');

    if (!sessionId) {
      const eventUserId = context.rootState.user?.currentEventUser?.eventUser?.id || '';
      sessionId = generateNewSessionIdForHeartbeats(context.rootState.route.params?.eventID?.toString(), eventUserId);
    }

    const payload = {
      payload_counter,
      session_id: sessionId,
      version: '1.1.0',
      logrocket_session_url: window.logrocketSessionUrl
    };

    context.commit('setHeartbeatMetadata', payload);
  },

  getPlayerSize(context: EventHeartbeatContext) {
    if (context.rootState.event?.stage?.fullScreen) {
      return 'FULL_SCREEN';
    }
    if (context.rootGetters['event/stage/isMinimized']) {
      return 'MINI';
    }
    return 'NORMAL';
  },

  getPrerecordedVideoStatus(context: EventHeartbeatContext, currentBroadcast: Broadcast) {
    try {
      const defaultState = {
        isSimuliveVideoPlaying: false,
        isLiveHybridVideoPlaying: false,
        isLiveExternalVideoPlaying: false
      };

      if (!currentBroadcast) {
        return defaultState;
      }

      const broadcastType = currentBroadcast?.broadcast_type;
      const isSimulive = broadcastType === 'SIMULIVE';
      const isLiveHybrid = broadcastType === 'LIVE_HYBRID';
      const isLiveExternal = broadcastType === 'LIVE_EXTERNAL';

      if (!isSimulive && !isLiveHybrid && !isLiveExternal) {
        return defaultState;
      }

      if (isLiveExternal) {
        return {
          isSimuliveVideoPlaying: false,
          isLiveHybridVideoPlaying: false,
          isLiveExternalVideoPlaying: !!currentBroadcast?.is_live
        };
      }

      if (isLiveHybrid) {
        const isLiveHybridVideoPlaying =
          isLiveHybrid && currentBroadcast?.current_playing_video_asset && currentBroadcast?.playback_start_time;

        return {
          isSimuliveVideoPlaying: false,
          isLiveHybridVideoPlaying,
          isLiveExternalVideoPlaying: false
        };
      }

      const currentUtcTimestamp = context.rootState.event.stage?.heartBeatDate?.getTime() || new Date().getTime();
      const broadcastStartUnixTimestamp = new Date(currentBroadcast?.start_time || '').getTime();
      const broadcastEndUnixTimestamp = new Date(currentBroadcast?.end_time || '').getTime();

      const isSimuliveVideoPlaying =
        isSimulive &&
        broadcastStartUnixTimestamp < currentUtcTimestamp &&
        currentUtcTimestamp < broadcastEndUnixTimestamp;

      return {
        isSimuliveVideoPlaying,
        isLiveHybridVideoPlaying: false,
        isLiveExternalVideoPlaying: false
      };
    } catch (error) {
      loggerService.error('error while calculating pre-recorded video state for heartbeat');
    }
  },

  getIsLiveStatusOfCurrentAgendaItem(context: EventHeartbeatContext) {
    const currentRoute = context.rootState.route;
    if (currentRoute.name == 'Broadcast') {
      const currentBroadcast: Broadcast = context.rootGetters['event/currentBroadcast'];
      return currentBroadcast?.is_live;
    }
    const items = context.rootGetters['event/userTrackAgenda'] || [];
    if (context.rootState.route.fullPath.includes('discussion-room')) {
      const currentDiscussionRoom = context.rootGetters['event/getDiscussionGroup']((currentRoute as any).params?.id);
      if (currentDiscussionRoom?.is_booth) {
        return !!items.find(item => item.type === 'BOOTH' && item.is_live);
      }
      if (!currentDiscussionRoom?.owner && !currentDiscussionRoom?.is_booth) {
        return !!items.find(item => item.type === 'DISCUSSION_GROUP' && item.is_live);
      }
    }
    if (
      currentRoute.name == 'EventBooth' ||
      currentRoute.name == 'EventBoothsGridPage' ||
      currentRoute.name == 'EventBooth2' ||
      currentRoute.name == 'EventBoothsGridPage2'
    ) {
      return !!items.find(item => item.type === 'BOOTH' && item.is_live);
    }
    if (currentRoute.name == 'DiscussionGroups') {
      return !!items.find(item => item.type === 'DISCUSSION_GROUP' && item.is_live);
    }
    return false;
  },
  async collectHeartbeatCurrentScreenState(context: EventHeartbeatContext) {
    const currentBroadcast: Broadcast = context.rootGetters['event/currentEventBroadcast'](
      context.rootState.route.params?.id
    );

    const stageSize = await context.dispatch('getPlayerSize');
    const isPrerecordedVideoPlaying = await context.dispatch('getPrerecordedVideoStatus', currentBroadcast);
    const currentAgendaItemIsLive = await context.dispatch('getIsLiveStatusOfCurrentAgendaItem');

    const payload: EventHeartbeatState['current_screen_state'] = {
      is_prerecorded_video_playing: {
        is_simulive_video_playing: !!isPrerecordedVideoPlaying.isSimuliveVideoPlaying,
        is_live_hybrid_video_playing: !!isPrerecordedVideoPlaying.isLiveHybridVideoPlaying,
        is_live_external_video_playing: !!isPrerecordedVideoPlaying.isLiveExternalVideoPlaying
      },
      is_on_demand_video_playing: isOnDemandVideoPlaying,
      current_route_name: context.rootState.route.name?.toString() || '',
      stage_player_size: stageSize,
      broadcast_id: currentBroadcast?.id || '',
      broadcast_type: currentBroadcast?.broadcast_type || '',
      booth_id:
        (context.rootState.route.params?.boothID as string) || (context.rootState.route.query?.boothID as string) || '',
      discussion_room_id:
        context.rootState.route.name == 'DiscussionRoom' ? context.rootState.route.params?.id.toString() : '',
      is_live: currentAgendaItemIsLive,
      video_length: videoLength || 0,
      seek_pos: videoSeekPosition || 0,
      sharing: {
        broadcast_ticker: !!currentBroadcast?.show_ticker,
        broadcast_lower_third: !!currentBroadcast?.lower_third_spec,
        event_ticker: !!context.rootState.event.event?.show_ticker
      },
      asset: null,
      chapter_id: null
    };

    context.commit('setHeartbeatCurrentScreenState', payload);
  },
  collectHeartbeatUserData(context: EventHeartbeatContext) {
    const eventUser = context.rootState.user?.currentEventUser?.eventUser;

    const payload: EventHeartbeatState['user_data'] = {
      event_user_id: eventUser?.id || '',
      last_interaction_time: new Date().getTime(),
      is_tab_active: window.document.visibilityState === 'visible',
      event_role: eventUser?.event_role
    };

    context.commit('setHeartbeatUserData', payload);
  },
  collectHeartbeatNetworkQuality(context: EventHeartbeatContext) {
    const collectedUplinkDataPoints = getNetworkQualityDatapoints().uplink;
    const collectedDownlinkDataPoints = getNetworkQualityDatapoints().downlink;

    const aggregatedUplinkData = getAggregate(
      collectedUplinkDataPoints,
      context.rootState.mediaroom.signalMetadata.networkQuality.uplinkNetworkQuality
    );

    const aggregatedDownlinkData = getAggregate(
      collectedDownlinkDataPoints,
      context.rootState.mediaroom.signalMetadata.networkQuality.downlinkNetworkQuality
    );

    return {
      uplink: aggregatedUplinkData,
      downlink: aggregatedDownlinkData
    };
  },
  async collectHeartbeatQos(context: EventHeartbeatContext) {
    const heartbeatNetworkQuality = await context.dispatch('collectHeartbeatNetworkQuality');

    const payload: Partial<EventHeartbeatState['qos']> = {
      is_litemode_on: context.rootState.user.liteMode.liteModeActive,
      network_quality: heartbeatNetworkQuality,
      memory: {
        jsHeapSizeLimit: (performance as any)?.memory?.jsHeapSizeLimit,
        totalJSHeapSize: (performance as any)?.memory?.totalJSHeapSize,
        usedJSHeapSize: (performance as any)?.memory?.usedJSHeapSize
      }
    };

    const currentMediaRoom: MediaRoom = context.rootGetters['mediaroom/currentMediaRoom'];
    logRecordingAndBroadcastLifecycle(context, currentMediaRoom);

    // ── Agora RTC metrics snapshot ────────────────────────────────────────
    if (currentMediaRoom && currentMediaRoom.client) {
      const agoraSnapshot = trackerService.getServiceSnapshot('agora');
      const isConnected = currentMediaRoom.client.connectionState === 'CONNECTED';
      const isScreenConnected = currentMediaRoom.screenShareClient?.connectionState === 'CONNECTED';

      const localAudioStats = isConnected ? currentMediaRoom.client.getLocalAudioStats() : null;
      const localVideoStats = isConnected ? currentMediaRoom.client.getLocalVideoStats() : null;
      const screenVideoStats = isScreenConnected ? currentMediaRoom.screenShareClient.getLocalVideoStats() : null;

      const rtcStats = isConnected ? (currentMediaRoom.client as any).getRTCStats?.() : null;

      const remoteVideoStats = isConnected ? currentMediaRoom.client.getRemoteVideoStats() : null;
      const remoteAudioStats = isConnected ? (currentMediaRoom.client as any).getRemoteAudioStats?.() : null;

      let worstRemoteVideoPacketLoss: number | null = null;
      let worstRemoteVideoFreezeRate: number | null = null;
      let avgRemoteVideoReceiveBitrate: number | null = null;
      let worstRemoteVideoEnd2EndDelay: number | null = null;
      if (remoteVideoStats) {
        const uids = Object.keys(remoteVideoStats);
        if (uids.length > 0) {
          let totalBitrate = 0;
          for (const uid of uids) {
            const s = remoteVideoStats[uid];
            const loss = s.packetLossRate ?? 0;
            const freeze = s.freezeRate ?? 0;
            const delay = s.end2EndDelay ?? 0;
            totalBitrate += s.receiveBitrate ?? 0;
            if (worstRemoteVideoPacketLoss === null || loss > worstRemoteVideoPacketLoss) {
              worstRemoteVideoPacketLoss = loss;
            }
            if (worstRemoteVideoFreezeRate === null || freeze > worstRemoteVideoFreezeRate) {
              worstRemoteVideoFreezeRate = freeze;
            }
            if (worstRemoteVideoEnd2EndDelay === null || delay > worstRemoteVideoEnd2EndDelay) {
              worstRemoteVideoEnd2EndDelay = delay;
            }
          }
          avgRemoteVideoReceiveBitrate = totalBitrate / uids.length;
        }
      }

      let worstRemoteAudioPacketLoss: number | null = null;
      let worstRemoteAudioFreezeRate: number | null = null;
      let worstRemoteAudioCurrentPacketLoss: number | null = null;
      let worstRemoteAudioEnd2EndDelay: number | null = null;
      let totalRemoteAudioPacketsDiscarded: number | null = null;
      if (remoteAudioStats) {
        const uids = Object.keys(remoteAudioStats);
        for (const uid of uids) {
          const s = remoteAudioStats[uid];
          // Preserve null when SDK hasn't populated a field yet (first ~3s after subscribe).
          // Coercing undefined → 0 makes "no data" indistinguishable from "healthy" in Metabase.
          if (typeof s.packetLossRate === 'number') {
            if (worstRemoteAudioPacketLoss === null || s.packetLossRate > worstRemoteAudioPacketLoss) {
              worstRemoteAudioPacketLoss = s.packetLossRate;
            }
          }
          if (typeof s.freezeRate === 'number') {
            if (worstRemoteAudioFreezeRate === null || s.freezeRate > worstRemoteAudioFreezeRate) {
              worstRemoteAudioFreezeRate = s.freezeRate;
            }
          }
          // currentPacketLossRate = rolling 400ms loss rate; better jitter signal than cumulative loss.
          if (typeof s.currentPacketLossRate === 'number') {
            if (
              worstRemoteAudioCurrentPacketLoss === null ||
              s.currentPacketLossRate > worstRemoteAudioCurrentPacketLoss
            ) {
              worstRemoteAudioCurrentPacketLoss = s.currentPacketLossRate;
            }
          }
          if (typeof s.end2EndDelay === 'number') {
            if (worstRemoteAudioEnd2EndDelay === null || s.end2EndDelay > worstRemoteAudioEnd2EndDelay) {
              worstRemoteAudioEnd2EndDelay = s.end2EndDelay;
            }
          }
          // receivePacketsDiscarded = packets dropped by jitter buffer (early/late arrival); real jitter proxy.
          if (typeof s.receivePacketsDiscarded === 'number') {
            totalRemoteAudioPacketsDiscarded = (totalRemoteAudioPacketsDiscarded ?? 0) + s.receivePacketsDiscarded;
          }
        }
      }

      // Network quality from mediaroom state
      const nq = context.rootState.mediaroom.signalMetadata?.networkQuality;

      payload.agora_metrics = {
        // Session-cumulative ──────────────────────────────────────────────
        startup_time_ms: Number(agoraSnapshot?.firstConnectionTime) || 0,
        reconnect_count: Number(agoraSnapshot?.retry) || 0,
        disconnection_count: Number(agoraSnapshot?.disconnections) || 0,
        error_count: Number(agoraSnapshot?.error) || 0,
        connection_established_count: Number(agoraSnapshot?.connectionEstablished) || 0,
        avg_connection_time_ms: Number(agoraSnapshot?.averageConnectionTime) || 0,
        is_connected: isConnected,
        remote_user_count: currentMediaRoom.client.remoteUsers.length,
        cloud_proxy: (currentMediaRoom.client as any)._cloudProxyServerMode !== 'disabled',
        current_background_filter: context.rootState.mediaroom.currentBackgroundFilter || '',
        rtt_ms: rtcStats?.RTT ?? null,
        outgoing_available_bandwidth: rtcStats?.OutgoingAvailableBandwidth ?? null,
        uplink_network_quality: nq?.uplinkNetworkQuality ?? 0,
        downlink_network_quality: nq?.downlinkNetworkQuality ?? 0,
        local_audio_send_bitrate: localAudioStats?.sendBitrate ?? null,
        local_audio_send_packets_lost: localAudioStats?.sendPacketsLost ?? null,
        // sendJitterMs = network jitter on the upstream audio path (ms). Primary local jitter signal.
        local_audio_send_jitter_ms: localAudioStats?.sendJitterMs ?? null,
        // sendRttMs = round-trip time on the upstream audio path (ms).
        local_audio_send_rtt_ms: localAudioStats?.sendRttMs ?? null,
        // currentPacketLossRate = rolling 400ms loss rate on the upstream audio path.
        local_audio_current_packet_loss_rate: localAudioStats?.currentPacketLossRate ?? null,
        local_video_send_bitrate: localVideoStats?.sendBitrate ?? null,
        local_video_send_fps: localVideoStats?.sendFrameRate ?? null,
        local_video_send_resolution_height: localVideoStats?.sendResolutionHeight ?? null,
        local_video_encode_delay: localVideoStats?.encodeDelay ?? null,
        local_video_total_freeze_time: localVideoStats?.totalFreezeTime ?? null,
        local_video_target_send_bitrate: localVideoStats?.targetSendBitrate ?? null,
        screenshare_send_bitrate: screenVideoStats?.sendBitrate ?? null,
        screenshare_send_fps: screenVideoStats?.sendFrameRate ?? null,
        screenshare_total_freeze_time: screenVideoStats?.totalFreezeTime ?? null,
        // Live snapshot — remote streams (aggregated) ────────────────────
        worst_remote_video_packet_loss_rate: worstRemoteVideoPacketLoss,
        worst_remote_video_freeze_rate: worstRemoteVideoFreezeRate,
        avg_remote_video_receive_bitrate: avgRemoteVideoReceiveBitrate,
        worst_remote_video_end2end_delay: worstRemoteVideoEnd2EndDelay,
        worst_remote_audio_packet_loss_rate: worstRemoteAudioPacketLoss,
        worst_remote_audio_freeze_rate: worstRemoteAudioFreezeRate,
        worst_remote_audio_current_packet_loss_rate: worstRemoteAudioCurrentPacketLoss,
        worst_remote_audio_end2end_delay: worstRemoteAudioEnd2EndDelay,
        total_remote_audio_packets_discarded: totalRemoteAudioPacketsDiscarded
      };

      logHealthDiagnostics(context, payload.agora_metrics);

      // Per-participant Agora metrics → Datadog. Host-only, gated by USE_AGORA_DATADOG.
      const agoraDatadogGates = {
        useAgoraDatadog: context.rootState.event?.useAgoraDatadog === true,
        isHost: currentMediaRoom.userRole === MediaUserRole.host,
        isConnected,
        hasSamples: hasAgoraSamples()
      };
      if (loggerService.datadogDebug && !Object.values(agoraDatadogGates).every(Boolean)) {
        // eslint-disable-next-line no-console
        console.log('[datadog] agora_participant_metrics skipped', agoraDatadogGates);
      }
      if (
        agoraDatadogGates.useAgoraDatadog &&
        agoraDatadogGates.isHost &&
        agoraDatadogGates.isConnected &&
        agoraDatadogGates.hasSamples
      ) {
        const samples = drainAgoraSamples();
        const lastSample = samples[samples.length - 1];
        const windowMs = windowElapsedMs(samples);
        const lastNq = lastSample.nq;
        const tickAt = new Date().toISOString();
        const observerEventUserId: string | null = context.rootState.user?.currentEventUser?.eventUser?.id ?? null;
        const commonFields = {
          schema_version: 2,
          sample_count: samples.length,
          elapsed_ms: windowMs,
          event_id: context.rootState.event?.event?.id ?? null,
          broadcast_id: context.state.current_screen_state.broadcast_id || currentMediaRoom.channelName || null,
          heartbeat_session_id: context.state.metadata?.session_id ?? null,
          observer_event_user_id: observerEventUserId,
          observer_role: 'host',
          room_type: 'MAIN_STAGE',
          channel_name: currentMediaRoom.channelName ?? null,
          is_page_visible: document.visibilityState === 'visible',
          logrocket_session_url: window.logrocketSessionUrl ?? null,
          network_quality_uplink: lastNq?.uplinkNetworkQuality ?? null,
          network_quality_downlink: lastNq?.downlinkNetworkQuality ?? null,
          captured_at: tickAt
        };

        // Record shape is owned by ./agoraRollup and pinned by its golden test.
        // Each record is emitted in its own try/catch so one bad stat object can
        // neither drop its siblings nor touch the heartbeat path.
        const records = buildAgoraParticipantRecords(samples, commonFields, {
          observerEventUserId,
          clientUid: String(currentMediaRoom.client.uid ?? ''),
          clientConnectionState: currentMediaRoom.client.connectionState,
          screenUid: String(currentMediaRoom.screenShareClient?.uid ?? ''),
          screenConnectionState: currentMediaRoom.screenShareClient?.connectionState ?? null
        });
        for (const record of records) {
          try {
            loggerService.metric('agora_participant_metrics', record);
          } catch (e) {
            loggerService.warn('Failed to track agora logs', e);
          }
        }
      }
    }

    logNetworkDiagnostics(context, heartbeatNetworkQuality);

    // ── Simulive HLS metrics snapshot ────────────────────────────────────────
    const isSimuliveActive = context.state.current_screen_state.is_prerecorded_video_playing.is_simulive_video_playing;
    const hlsInstance = context.rootState.simuliveroom?.hLSContext;
    const hlsSnapshot = trackerService.getServiceSnapshot('inHouseSimulive');
    if (hlsSnapshot && isSimuliveActive) {
      payload.simulive_hls_metrics = {
        startup_time_ms: Number(hlsSnapshot.firstConnectionTime) || 0,
        buffer_count: Number(hlsSnapshot.bufferCount) || 0,
        genuine_buffer_duration_ms: Number(hlsSnapshot.totalBufferDuration) || 0,
        total_dropped_frames: Number(hlsSnapshot.droppedFrames) || 0,
        avg_live_latency_s: Number(hlsSnapshot.avgLiveLatency) || 0,
        non_fatal_error_count: Number(hlsSnapshot.nonFatalErrorCount) || 0,
        level_switch_down_count: Number(hlsSnapshot.levelSwitchDownCount) || 0, // total level_switch_count removed — upward switches are healthy
        playback_restart_count: Number(hlsSnapshot.playbackRestartCount) || 0,
        avg_frag_load_time_ms: Number(hlsSnapshot.avgFragLoadTime) || 0,
        current_latency_s: hlsInstance?.latency ?? null,
        current_bandwidth_bps: hlsInstance?.bandwidthEstimate ?? null, // current_level removed — index meaningless without levels[] array
        buffer_length_s: hlsInstance?.mainForwardBufferInfo?.len ?? null
      };
    }

    // ── On-demand HLS metrics snapshot ──────────────────────────────────────────
    const onDemandSnapshot = trackerService.getServiceSnapshot('onDemandVideo');
    const onDemandVideoElement = document
      .getElementById('onDemandVideoContainer')
      ?.querySelector('video') as HTMLVideoElement | null;
    const onDemandHlsInstance = onDemandVideoElement ? (onDemandVideoElement as any).__hlsInstance : null;
    if (onDemandSnapshot && isOnDemandVideoPlaying) {
      payload.ondemand_hls_metrics = {
        startup_time_ms: Number(onDemandSnapshot.firstConnectionTime) || 0,
        rebuffer_count: Number(onDemandSnapshot.bufferCount) || 0,
        rebuffer_duration_ms: Number(onDemandSnapshot.totalBufferDuration) || 0,
        total_dropped_frames: Number(onDemandSnapshot.droppedFrames) || 0,
        level_switch_down_count: Number(onDemandSnapshot.levelSwitchDownCount) || 0,
        avg_frag_load_time_ms: Number(onDemandSnapshot.avgFragLoadTime) || 0,
        non_fatal_error_count: Number(onDemandSnapshot.nonFatalErrorCount) || 0,
        seek_count: Number(onDemandSnapshot.seekCount) || 0,
        forward_skip_count: Number(onDemandSnapshot.forwardSkipCount) || 0,
        rewind_count: Number(onDemandSnapshot.rewindCount) || 0,
        pause_count: Number(onDemandSnapshot.pauseCount) || 0,
        completion_rate: videoLength > 0 ? videoSeekPosition / videoLength : null,
        current_position_s: videoSeekPosition || null,
        current_playback_speed: onDemandVideoElement?.playbackRate ?? null,
        current_bandwidth_bps: onDemandHlsInstance?.bandwidthEstimate ?? null,
        buffer_length_s: onDemandHlsInstance?.mainForwardBufferInfo?.len ?? null
      };
    }

    // ── Live-hybrid HLS metrics snapshot ────────────────────────────────────
    const liveHlsSnapshot = trackerService.getServiceSnapshot('liveHybridVideo');
    const liveHlsInstance = context.rootState.liveVideoRoom?.hlsContext;
    if (liveHlsSnapshot && context.rootState.liveVideoRoom?.currentPlayingVideoAsset) {
      payload.live_hls_metrics = {
        startup_time_ms: Number(liveHlsSnapshot.firstConnectionTime) || 0,
        buffer_count: Number(liveHlsSnapshot.bufferCount) || 0,
        buffer_duration_ms: Number(liveHlsSnapshot.totalBufferDuration) || 0,
        total_dropped_frames: Number(liveHlsSnapshot.droppedFrames) || 0,
        level_switch_down_count: Number(liveHlsSnapshot.levelSwitchDownCount) || 0,
        avg_frag_load_time_ms: Number(liveHlsSnapshot.avgFragLoadTime) || 0,
        non_fatal_error_count: Number(liveHlsSnapshot.nonFatalErrorCount) || 0,
        sync_correction_count: Number(liveHlsSnapshot.syncCorrectionCount) || 0,
        current_bandwidth_bps: liveHlsInstance?.bandwidthEstimate ?? null,
        buffer_length_s: liveHlsInstance?.mainForwardBufferInfo?.len ?? null
      };
    }

    // ── RTMP (LIVE_EXTERNAL) HLS metrics snapshot ────────────────────────────
    // RTMP HLS instance is emitted from HlsVideoPlayer → HlsRoom → BroadcastV2
    // and stored in liveVideoRoom.rtmpHlsContext. Tracker events go to
    // liveHybridVideo (set via setRtmpHlsContext action on HLS creation).
    const isRtmpActive = context.state.current_screen_state.is_prerecorded_video_playing.is_live_external_video_playing;
    const rtmpHlsSnapshot = trackerService.getServiceSnapshot('liveHybridVideo');
    const rtmpHlsInstance = context.rootState.liveVideoRoom?.rtmpHlsContext;
    if (rtmpHlsSnapshot && isRtmpActive) {
      payload.rtmp_hls_metrics = {
        startup_time_ms: Number(rtmpHlsSnapshot.firstConnectionTime) || 0,
        buffer_count: Number(rtmpHlsSnapshot.bufferCount) || 0,
        genuine_buffer_duration_ms: Number(rtmpHlsSnapshot.totalBufferDuration) || 0,
        total_dropped_frames: Number(rtmpHlsSnapshot.droppedFrames) || 0,
        avg_live_latency_s: Number(rtmpHlsSnapshot.avgLiveLatency) || 0,
        non_fatal_error_count: Number(rtmpHlsSnapshot.nonFatalErrorCount) || 0,
        level_switch_down_count: Number(rtmpHlsSnapshot.levelSwitchDownCount) || 0,
        playback_restart_count: Number(rtmpHlsSnapshot.playbackRestartCount) || 0,
        avg_frag_load_time_ms: Number(rtmpHlsSnapshot.avgFragLoadTime) || 0,
        current_latency_s: rtmpHlsInstance?.latency ?? null,
        current_bandwidth_bps: rtmpHlsInstance?.bandwidthEstimate ?? null,
        buffer_length_s: rtmpHlsInstance?.mainForwardBufferInfo?.len ?? null
      };
    }

    context.commit('setHeartbeatQos', payload);
  }
};

const healthDiagnosticState = new Map<
  HealthDiagnosticSignal,
  {
    poor: boolean;
    lastEmittedAt: number;
    pending: boolean;
  }
>();
const lifecycleDiagnosticState = new Map<string, { state: string; lastEmittedAt: number }>();
const recorderLifecycleDiagnosticState = new Map<
  string,
  {
    isRecording: boolean;
    recordingState: string;
    failureObserved: boolean;
  }
>();

function logAppAction(metadata: Record<string, string | number | boolean>) {
  try {
    loggerService.info('app_action', metadata);
  } catch {
    // Operational diagnostics must never affect the heartbeat or media path.
  }
}

function logLifecycleDiagnostic(context: EventHeartbeatContext, state: BroadcastConnectionLifecycleState) {
  const broadcast = context.rootGetters['event/currentBroadcast'];
  const broadcastId = broadcast?.id || context.state.current_screen_state?.broadcast_id || '';
  if (!broadcastId) {
    return;
  }

  const key = `broadcast_connection:${broadcastId}`;
  const now = Date.now();
  const previous = lifecycleDiagnosticState.get(key);
  if (previous?.state === state && now - previous.lastEmittedAt < HEALTH_DIAGNOSTIC_COOLDOWN_MS) {
    return;
  }

  lifecycleDiagnosticState.set(key, { state, lastEmittedAt: now });
  logAppAction({
    action: `broadcast_connection_${state.toLowerCase()}`,
    broadcast_id: broadcastId,
    event_id: context.rootState.event?.event?.id || ''
  });
}

function logRecorderLifecycle(context: EventHeartbeatContext) {
  const broadcast = context.rootGetters['event/currentBroadcast'];
  const broadcastId = broadcast?.id || context.state.current_screen_state?.broadcast_id || '';
  if (!broadcastId || typeof broadcast?.is_recording !== 'boolean') {
    return;
  }

  const recordingState = broadcast.recording_studio_state;
  const key = `recorder:${broadcastId}`;
  const previous = recorderLifecycleDiagnosticState.get(key);
  const isActiveOrStarting =
    recordingState === RECORDING_STUDIO_STATE.RECORDING || recordingState === RECORDING_STUDIO_STATE.STARTING;
  const isFailure = !!previous?.isRecording && !broadcast.is_recording && isActiveOrStarting;
  const isRestart =
    !!previous?.failureObserved && broadcast.is_recording && recordingState === RECORDING_STUDIO_STATE.RECORDING;

  recorderLifecycleDiagnosticState.set(key, {
    isRecording: broadcast.is_recording,
    recordingState,
    failureObserved: isRestart ? false : !!previous?.failureObserved || isFailure
  });

  if (!isFailure && !isRestart) {
    return;
  }
  logAppAction({
    action: isRestart ? 'recorder_restarted' : 'recorder_failed',
    broadcast_id: broadcastId,
    event_id: context.rootState.event?.event?.id || ''
  });
}

function logRecordingAndBroadcastLifecycle(context: EventHeartbeatContext, currentMediaRoom: MediaRoom | null) {
  logRecorderLifecycle(context);
  const connectionState = currentMediaRoom?.client?.connectionState as BroadcastConnectionLifecycleState | undefined;
  if (
    connectionState === 'CONNECTED' ||
    connectionState === 'CONNECTING' ||
    connectionState === 'RECONNECTING' ||
    connectionState === 'DISCONNECTING' ||
    connectionState === 'DISCONNECTED'
  ) {
    logLifecycleDiagnostic(context, connectionState);
  }
}

function logHealthDiagnostic(
  context: EventHeartbeatContext,
  signal: HealthDiagnosticSignal,
  poor: boolean,
  metric: string,
  value: number
) {
  const now = Date.now();
  const previous = healthDiagnosticState.get(signal);
  if (!previous && !poor) {
    healthDiagnosticState.set(signal, { poor, lastEmittedAt: 0, pending: false });
    return;
  }
  if (previous?.poor === poor && !previous.pending) {
    return;
  }
  if (previous && now - previous.lastEmittedAt < HEALTH_DIAGNOSTIC_COOLDOWN_MS) {
    healthDiagnosticState.set(signal, { poor, lastEmittedAt: previous.lastEmittedAt, pending: true });
    return;
  }

  healthDiagnosticState.set(signal, { poor, lastEmittedAt: now, pending: false });
  logAppAction({
    action: poor ? 'stream_health_degraded' : 'stream_health_recovered',
    signal,
    metric,
    value,
    event_id: context.rootState.event?.event?.id || '',
    broadcast_id: context.state.current_screen_state?.broadcast_id || ''
  });
}

function logNetworkDiagnostics(context: EventHeartbeatContext, networkQuality: any) {
  const uplink = Number(networkQuality?.uplink?.current);
  const downlink = Number(networkQuality?.downlink?.current);
  if (Number.isFinite(uplink)) {
    logHealthDiagnostic(
      context,
      'poor_uplink_connection',
      uplink >= POOR_NETWORK_QUALITY_THRESHOLD,
      'uplink_network_quality',
      uplink
    );
  }
  if (Number.isFinite(downlink)) {
    logHealthDiagnostic(
      context,
      'poor_downlink_connection',
      downlink >= POOR_NETWORK_QUALITY_THRESHOLD,
      'downlink_network_quality',
      downlink
    );
  }
}

function logHealthDiagnostics(
  context: EventHeartbeatContext,
  metrics: NonNullable<EventHeartbeatState['qos']['agora_metrics']>
) {
  const signals: Array<[HealthDiagnosticSignal, number | null, number, boolean]> = [
    ['stream_rtt', metrics.rtt_ms, STREAM_HEALTH_THRESHOLDS.rtt_ms, true],
    ['stream_local_video_fps', metrics.local_video_send_fps, STREAM_HEALTH_THRESHOLDS.local_video_send_fps, false],
    [
      'stream_remote_video_packet_loss',
      metrics.worst_remote_video_packet_loss_rate,
      STREAM_HEALTH_THRESHOLDS.remote_video_packet_loss_rate,
      true
    ],
    [
      'stream_remote_audio_packet_loss',
      metrics.worst_remote_audio_packet_loss_rate,
      STREAM_HEALTH_THRESHOLDS.remote_audio_packet_loss_rate,
      true
    ]
  ];

  signals.forEach(([signal, value, threshold, isUpperBound]) => {
    if (typeof value !== 'number') {
      return;
    }
    logHealthDiagnostic(context, signal, isUpperBound ? value > threshold : value < threshold, signal, value);
  });
}

export function resetHealthDiagnosticState() {
  healthDiagnosticState.clear();
}

export function resetLifecycleDiagnosticState() {
  lifecycleDiagnosticState.clear();
  recorderLifecycleDiagnosticState.clear();
}

function generateNewSessionIdForHeartbeats(eventId: string, eventUserId: string) {
  const currentTimestamp = Date.now();
  const sessionId = `${eventId}|${eventUserId}|${currentTimestamp}`;

  window.sessionStorage.setItem('segmentSessionId', sessionId);
  return sessionId;
}

function getEventHeartbeatFastPayload(heartbeatPayload: EventHeartbeatState, context: EventHeartbeatContext) {
  const currentUser = context.rootGetters['getCurrentUser'];
  const event = context.rootGetters['event/currentEvent'];
  const currentBroadcast = context.rootGetters['event/currentBroadcast'];
  return {
    ...heartbeatPayload,
    event_info: {
      ...heartbeatPayload.event_info,
      event_title: event?.title ?? ''
    },
    current_screen_state: {
      ...heartbeatPayload.current_screen_state,
      broadcast_title: currentBroadcast?.title ?? ''
    },
    user_data: {
      ...heartbeatPayload.user_data,
      meta_data: {
        first_name: currentUser?.first_name,
        last_name: currentUser?.last_name,
        company: currentUser?.company,
        props: currentUser?.eventUser?.props ?? {}
      }
    }
  };
}

export default actions;
